// src/pages/dashboard/accounts/AccountList.js  (v2.0 – modernized UI)
import React from 'react'
import { getPlatformIcon, getPlatformColor } from './helpers'

const PLATFORM_LABELS = {
  facebook  : 'Facebook',
  instagram : 'Instagram',
  threads   : 'Threads',
  x         : 'X / Twitter',
  linkedin  : 'LinkedIn',
  tiktok    : 'TikTok',
  pinterest : 'Pinterest',
  youtube   : 'YouTube',
}

function getAvatar (acc, item) {
  const p = acc.platform.toLowerCase()
  if (p === 'facebook')  return item.pictureUrl       || item.profileImage    || '/img/default-avatar.png'
  if (p === 'instagram') return item.igProfilePic     || '/img/default-avatar.png'
  if (p === 'threads')   return item.thProfilePic     || '/img/default-avatar.png'
  if (p === 'x')         return item.profileImage     || '/img/default-avatar.png'
  if (p === 'linkedin')  return item.profileImage     || item.logoUrl         || '/img/default-avatar.png'
  if (p === 'tiktok')    return item.profileImage     || '/img/default-avatar.png'
  if (p === 'pinterest') return item.profileImage     || '/img/default-avatar.png'
  if (p === 'youtube')   return item.channelThumbnail || '/img/default-avatar.png'
  return '/img/default-avatar.png'
}

function getItemKey (acc, item) {
  const p = acc.platform.toLowerCase()
  if (p === 'facebook')  return item.id
  if (p === 'instagram') return item.igId
  if (p === 'threads')   return item.thId
  if (p === 'x')         return item.twitterId
  if (p === 'linkedin')  return item.lnId || item.id
  if (p === 'tiktok')    return item.tiktokId
  if (p === 'pinterest') return item.pinterestId
  if (p === 'youtube')   return item.youtubeId
  return Math.random()
}

function getItemName (acc, item) {
  const p = acc.platform.toLowerCase()
  if (p === 'facebook')  return item.name            || item.displayName || ''
  if (p === 'instagram') return item.igUsername      || ''
  if (p === 'threads')   return item.thUsername      || ''
  if (p === 'x')         return item.username        || item.displayName || ''
  if (p === 'linkedin')  return item.displayName     || item.name        || ''
  if (p === 'tiktok')    return item.displayName     || item.username    || ''
  if (p === 'pinterest') return item.displayName     || item.username    || ''
  if (p === 'youtube')   return item.channelTitle    || item.displayName || ''
  return ''
}

export default function AccountList ({ accounts = [], dropdowns, onReconnect }) {
  if (!accounts.length) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {accounts.map((acc, idx) => {
        const platLower      = acc.platform.toLowerCase()
        const iconClass      = getPlatformIcon(acc.platform)
        const iconColor      = getPlatformColor(acc.platform)
        const renderDropdown = dropdowns[platLower] ? dropdowns[platLower]() : null
        const label          = PLATFORM_LABELS[platLower] || acc.platform

        return (
          <div
            key={idx}
            style={{
              display      : 'flex',
              alignItems   : 'center',
              gap          : 16,
              background   : '#ffffff',
              border       : '1px solid #e5e7eb',
              borderRadius : 12,
              padding      : '14px 18px',
              boxShadow    : '0 1px 3px rgba(0,0,0,0.05)',
              flexWrap     : 'wrap',
            }}
          >
            {/* Platform icon badge */}
            <div style={{
              width        : 42,
              height       : 42,
              borderRadius : 10,
              background   : `${iconColor}15`,
              display      : 'flex',
              alignItems   : 'center',
              justifyContent: 'center',
              flexShrink   : 0,
            }}>
              <i className={iconClass} style={{ fontSize: 20, color: iconColor }} />
            </div>

            {/* Platform label */}
            <div style={{ minWidth: 90, flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                {label}
              </span>
            </div>

            {/* Connected page avatars */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap' }}>
              {acc.connectedPages?.length > 0 ? (
                acc.connectedPages.map(item => {
                  const src  = getAvatar(acc, item)
                  const key  = getItemKey(acc, item)
                  const name = getItemName(acc, item)

                  return (
                    <div
                      key={key}
                      style={{ display: 'flex', alignItems: 'center', gap: 7 }}
                      title={name}
                    >
                      <div style={{ position: 'relative' }}>
                        <img
                          src={src}
                          alt={name}
                          style={{
                            width       : 32,
                            height      : 32,
                            borderRadius: '50%',
                            objectFit   : 'cover',
                            border      : '2px solid #e5e7eb',
                            opacity     : item.needsReconnect ? 0.55 : 1,
                            filter      : item.needsReconnect ? 'grayscale(1)' : 'none',
                          }}
                          onError={e => { e.target.src = '/img/default-avatar.png' }}
                        />
                        {/* Platform badge on avatar */}
                        <div style={{
                          position      : 'absolute',
                          bottom        : -2,
                          right         : -2,
                          width         : 14,
                          height        : 14,
                          borderRadius  : '50%',
                          background    : '#fff',
                          border        : '1.5px solid #e5e7eb',
                          display       : 'flex',
                          alignItems    : 'center',
                          justifyContent: 'center',
                        }}>
                          <i className={iconClass} style={{ fontSize: 7, color: iconColor }} />
                        </div>
                      </div>
                      {name && (
                        <span style={{
                          fontSize    : 12,
                          fontWeight  : 500,
                          color       : '#6b7280',
                          maxWidth    : 120,
                          overflow    : 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace  : 'nowrap',
                        }}>
                          {name}
                        </span>
                      )}
                      {item.needsReconnect && (
                        <button
                          onClick={() => onReconnect && onReconnect(acc.platform)}
                          title="This connection has expired — click to reconnect"
                          style={{
                            display      : 'inline-flex',
                            alignItems   : 'center',
                            gap          : 5,
                            marginLeft   : 2,
                            padding      : '3px 9px',
                            borderRadius : 99,
                            border       : '1px solid #fecaca',
                            background   : '#fef2f2',
                            color        : '#dc2626',
                            fontSize     : 11,
                            fontWeight   : 700,
                            cursor       : 'pointer',
                            whiteSpace   : 'nowrap',
                          }}
                        >
                          <i className='fas fa-triangle-exclamation' style={{ fontSize: 10 }} />
                          Reconnect
                        </button>
                      )}
                    </div>
                  )
                })
              ) : (
                <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                  No pages connected
                </span>
              )}
            </div>

            {/* Dropdown menu — pushed to far right */}
            {renderDropdown && (
              <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                {renderDropdown}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}